import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { IMAGES, waLink, WA_MESSAGES } from "../../data";

const LINES = [
  { text: "SCAFFOLDING SIAP", cls: "text-white" },
  { text: "MENDUKUNG", cls: "text-stroke-light" },
  { text: "PROYEK ANDA.", cls: "text-[#DF9F00]" },
];

const ease = [0.16, 1, 0.3, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.1]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="beranda"
      ref={ref}
      data-testid="hero-section"
      className="relative flex h-[100svh] min-h-[600px] flex-col justify-center overflow-hidden bg-[#0F1115] pt-16"
    >
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={IMAGES.crew}
          alt="Dokumentasi proyek konstruksi dengan scaffolding dan tower crane"
          className="h-full w-full object-cover"
          data-testid="hero-background-image"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] via-transparent to-black/30" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 lg:px-8"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35, ease }}
          data-testid="hero-eyebrow"
          className="font-tech mb-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/70"
        >
          <span className="inline-block h-[2px] w-10 bg-[#DF9F00]" />
          CV Mitra Bangun Utama — Rental &amp; Sales
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display max-w-4xl text-[56px] font-semibold uppercase leading-[0.98] tracking-wide sm:text-7xl lg:text-8xl"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.45 + i * 0.14, ease }}
                className={`block pr-2 ${line.cls}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease }}
          data-testid="hero-subheadline"
          className="mt-6 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
        >
          Sewa &amp; jual scaffolding berkualitas, lengkap dan siap diantar ke lokasi
          proyek Anda.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2, ease }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={waLink(WA_MESSAGES.rental)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-sewa"
            className="group flex items-center gap-3 rounded-lg bg-[#DF9F00] px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#141619] transition-colors duration-300 hover:bg-white"
          >
            Sewa Scaffolding
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href={waLink(WA_MESSAGES.general)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-hubungi"
            className="flex items-center gap-3 rounded-lg border border-white/40 px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-white hover:bg-white hover:text-[#141619]"
          >
            <MessageCircle className="h-4 w-4" />
            Hubungi Kami
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 right-6 z-10 hidden flex-col items-center gap-3 lg:flex"
        aria-hidden="true"
      >
        <span className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/50 [writing-mode:vertical-lr]">
          Scroll
        </span>
        <span className="animate-scroll-line block h-14 w-[2px] bg-[#DF9F00]" />
      </motion.div>
    </section>
  );
};

export default Hero;
